/* eslint-disable @typescript-eslint/no-use-before-define */

import {
  filter,
  make,
  makeSubject,
  onEnd,
  onPush,
  onStart,
  pipe,
  share,
  Source,
  take,
  takeUntil,
  publish,
  subscribe,
  switchMap,
  fromValue,
  merge,
  map,
  Subscription,
} from 'wonka';

import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { DocumentNode } from 'graphql';

import { composeExchanges, defaultExchanges } from './exchanges';
import { fallbackExchange } from './exchanges/fallback';

import {
  AnyVariables,
  Exchange,
  ExchangeInput,
  GraphQLRequest,
  Operation,
  OperationContext,
  OperationResult,
  OperationType,
  RequestPolicy,
  PromisifiedSource,
  DebugEvent,
} from './types';

import {
  createRequest,
  withPromise,
  maskTypename,
  noop,
  makeOperation,
  getOperationType,
} from './utils';

/** Options for configuring the URQL [client]{@link Client}. */
export interface ClientOptions {
  /** Target endpoint URL such as `https://my-target:8080/graphql`. */
  url: string;
  /** Any additional options to pass to fetch. */
  fetchOptions?: RequestInit | (() => RequestInit);
  /** An alternative fetch implementation. */
  fetch?: typeof fetch;
  /** An ordered array of Exchanges. */
  exchanges?: Exchange[];
  /** Activates support for Suspense. */
  suspense?: boolean;
  /** The default request policy for requests. */
  requestPolicy?: RequestPolicy;
  /** Use HTTP GET for queries. */
  preferGetMethod?: boolean;
  /** Mask __typename from results. */
  maskTypename?: boolean;
}

export interface Client {
  new (options: ClientOptions): Client;

  operations$: Source<Operation>;
  suspense: boolean;

  /** Start an operation from an exchange */
  reexecuteOperation: (operation: Operation) => void;
  /** Event target for monitoring, e.g. for @urql/devtools */
  subscribeToDebugTarget?: (onEvent: (e: DebugEvent) => void) => Subscription;

  createRequestOperation<
    Data = any,
    Variables extends AnyVariables = AnyVariables
  >(
    kind: OperationType,
    request: GraphQLRequest<Data, Variables>,
    opts?: Partial<OperationContext> | undefined
  ): Operation<Data, Variables>;

  /** Executes an Operation by sending it through the exchange pipeline It returns an observable that emits all related exchange results and keeps track of this observable's subscribers. A teardown signal will be emitted when no subscribers are listening anymore. */
  executeRequestOperation<
    Data = any,
    Variables extends AnyVariables = AnyVariables
  >(
    operation: Operation<Data, Variables>
  ): Source<OperationResult<Data, Variables>>;

  query<Data = any, Variables extends AnyVariables = AnyVariables>(
    query: DocumentNode | TypedDocumentNode<Data, Variables> | string,
    variables: Variables,
    context?: Partial<OperationContext>
  ): PromisifiedSource<OperationResult<Data, Variables>>;

  readQuery<Data = any, Variables extends AnyVariables = AnyVariables>(
    query: DocumentNode | TypedDocumentNode<Data, Variables> | string,
    variables: Variables,
    context?: Partial<OperationContext>
  ): OperationResult<Data, Variables> | null;

  executeQuery<Data = any, Variables extends AnyVariables = AnyVariables>(
    query: GraphQLRequest<Data, Variables>,
    opts?: Partial<OperationContext> | undefined
  ): Source<OperationResult<Data, Variables>>;

  subscription<Data = any, Variables extends AnyVariables = AnyVariables>(
    query: DocumentNode | TypedDocumentNode<Data, Variables> | string,
    variables: Variables,
    context?: Partial<OperationContext>
  ): Source<OperationResult<Data, Variables>>;

  executeSubscription<
    Data = any,
    Variables extends AnyVariables = AnyVariables
  >(
    query: GraphQLRequest<Data, Variables>,
    opts?: Partial<OperationContext> | undefined
  ): Source<OperationResult<Data, Variables>>;

  mutation<Data = any, Variables extends AnyVariables = AnyVariables>(
    query: DocumentNode | TypedDocumentNode<Data, Variables> | string,
    variables: Variables,
    context?: Partial<OperationContext>
  ): PromisifiedSource<OperationResult<Data, Variables>>;

  executeMutation<Data = any, Variables extends AnyVariables = AnyVariables>(
    query: GraphQLRequest<Data, Variables>,
    opts?: Partial<OperationContext> | undefined
  ): Source<OperationResult<Data, Variables>>;
}

export const Client: new (opts: ClientOptions) => Client = function Client(
  this: Client | {},
  opts: ClientOptions
) {
  if (process.env.NODE_ENV !== 'production' && !opts.url) {
    throw new Error('You are creating an urql-client without a url.');
  }

  const replays = new Map<number, OperationResult>();
  const active: Map<number, Source<OperationResult>> = new Map();
  const queue: Operation[] = [];

  const baseOpts = {
    url: opts.url,
    fetchOptions: opts.fetchOptions,
    fetch: opts.fetch,
    preferGetMethod: !!opts.preferGetMethod,
    requestPolicy: opts.requestPolicy || 'cache-first',
  };

  // This subject forms the input of operations; executeOperation may be
  // called to dispatch a new operation on the subject
  const { source: operations$, next: nextOperation } = makeSubject<Operation>();

  // We define a queued dispatcher on the subject, which empties the queue when it's
  // activated to allow `reexecuteOperation` to be trampoline-scheduled
  let isOperationBatchActive = false;
  function dispatchOperation(operation?: Operation | void) {
    if (operation) nextOperation(operation);
    if (!isOperationBatchActive) {
      isOperationBatchActive = true;
      while (isOperationBatchActive && (operation = queue.shift()))
        nextOperation(operation);
      isOperationBatchActive = false;
    }
  }

  /** Defines how result streams are created */
  const makeResultSource = (operation: Operation) => {
    let result$ = pipe(
      results$,
      filter((res: OperationResult) => {
        return (
          res.operation.kind === operation.kind &&
          res.operation.key === operation.key &&
          (!res.operation.context._instance ||
            res.operation.context._instance === operation.context._instance)
        );
      })
    );

    // Mask typename properties if the option for it is turned on
    if (opts.maskTypename) {
      result$ = pipe(
        result$,
        map(res => ({ ...res, data: maskTypename(res.data, true) }))
      );
    }

    // A mutation is always limited to just a single result and is never shared
    if (operation.kind === 'mutation') {
      return pipe(
        result$,
        onStart(() => nextOperation(operation)),
        take(1)
      );
    }

    const source = pipe(
      result$,
      // End the results stream when an active teardown event is sent
      takeUntil(
        pipe(
          operations$,
          filter(op => op.kind === 'teardown' && op.key === operation.key)
        )
      ),
      switchMap(result => {
        if (operation.kind !== 'query' || result.stale) {
          return fromValue(result);
        }

        return merge([
          fromValue(result),
          // Mark a result as stale when a new operation is sent for it
          pipe(
            operations$,
            filter(
              op =>
                op.kind === 'query' &&
                op.key === operation.key &&
                op.context.requestPolicy !== 'cache-only'
            ),
            take(1),
            map(() => ({ ...result, stale: true }))
          ),
        ]);
      }),
      onPush(result => {
        replays.set(operation.key, result);
      }),
      onEnd(() => {
        // Delete the active operation handle
        replays.delete(operation.key);
        active.delete(operation.key);
        // Delete all queued up operations of the same key on end
        for (let i = queue.length - 1; i >= 0; i--)
          if (queue[i].key === operation.key) queue.splice(i, 1);
        // Dispatch a teardown signal for the stopped operation
        nextOperation(makeOperation('teardown', operation, operation.context));
      }),
      share
    );

    return source;
  };

  const instance: Client =
    this instanceof Client ? this : Object.create(Client.prototype);
  const client: Client = Object.assign(instance, {
    suspense: !!opts.suspense,
    operations$,

    reexecuteOperation(operation: Operation) {
      // Reexecute operation only if any subscribers are still subscribed to the
      // operation's exchange results
      if (operation.kind === 'mutation' || active.has(operation.key)) {
        queue.push(operation);
        Promise.resolve().then(dispatchOperation);
      }
    },

    createRequestOperation(kind, request, opts) {
      if (!opts) opts = {};
      const requestOperationType = getOperationType(request.query);
      if (
        process.env.NODE_ENV !== 'production' &&
        kind !== 'teardown' &&
        requestOperationType !== kind
      ) {
        throw new Error(
          `Expected operation of type "${kind}" but found "${requestOperationType}"`
        );
      }
      return makeOperation(kind, request, {
        _instance: kind === 'mutation' ? [] : undefined,
        ...baseOpts,
        ...opts,
        requestPolicy: opts.requestPolicy || baseOpts.requestPolicy,
        suspense: opts.suspense || (opts.suspense !== false && client.suspense),
      });
    },

    executeRequestOperation(operation) {
      if (operation.kind === 'mutation') {
        return makeResultSource(operation);
      }

      return make<OperationResult>(observer => {
        let source = active.get(operation.key);

        if (!source) {
          active.set(operation.key, (source = makeResultSource(operation)));
        }

        const isNetworkOperation =
          operation.context.requestPolicy === 'cache-and-network' ||
          operation.context.requestPolicy === 'network-only';

        return pipe(
          source,
          onStart(() => {
            const prevReplay = replays.get(operation.key);

            if (operation.kind === 'subscription') {
              return dispatchOperation(operation);
            } else if (isNetworkOperation) {
              dispatchOperation(operation);
            }

            if (
              prevReplay != null &&
              prevReplay === replays.get(operation.key)
            ) {
              observer.next(
                isNetworkOperation ? { ...prevReplay, stale: true } : prevReplay
              );
            } else if (!isNetworkOperation) {
              dispatchOperation(operation);
            }
          }),
          onEnd(() => {
            isOperationBatchActive = false;
            observer.complete();
          }),
          subscribe(observer.next)
        ).unsubscribe;
      });
    },

    executeQuery(query, opts) {
      const operation = client.createRequestOperation('query', query, opts);
      return client.executeRequestOperation(operation);
    },

    executeSubscription(query, opts) {
      const operation = client.createRequestOperation(
        'subscription',
        query,
        opts
      );
      return client.executeRequestOperation(operation);
    },

    executeMutation(query, opts) {
      const operation = client.createRequestOperation('mutation', query, opts);
      return client.executeRequestOperation(operation);
    },

    query(query, variables, context) {
      if (!context || typeof context.suspense !== 'boolean') {
        context = { ...context, suspense: false };
      }

      return withPromise(
        client.executeQuery(createRequest(query, variables), context)
      );
    },

    readQuery(query, variables, context) {
      let result: OperationResult | null = null;

      pipe(
        client.query(query, variables, context),
        subscribe(res => {
          result = res;
        })
      ).unsubscribe();

      return result;
    },

    subscription(query, variables, context) {
      return client.executeSubscription(
        createRequest(query, variables),
        context
      );
    },

    mutation(query, variables, context) {
      return withPromise(
        client.executeMutation(createRequest(query, variables), context)
      );
    },
  } as Client);

  let dispatchDebug: ExchangeInput['dispatchDebug'] = noop;
  if (process.env.NODE_ENV !== 'production') {
    const { next, source } = makeSubject<DebugEvent>();
    client.subscribeToDebugTarget = (onEvent: (e: DebugEvent) => void) =>
      pipe(source, subscribe(onEvent));
    dispatchDebug = next as ExchangeInput['dispatchDebug'];
  }

  const exchanges =
    opts.exchanges !== undefined ? opts.exchanges : defaultExchanges;

  // All exchange are composed into a single one and are called using the constructed client
  // and the fallback exchange stream
  const composedExchange = composeExchanges(exchanges);

  // All exchanges receive inputs using which they can forward operations to the next exchange
  // and receive a stream of results in return, access the client, or dispatch debugging events
  // All operations then run through the Exchange IOs in a pipeline-like fashion
  const results$ = share(
    composedExchange({
      client,
      dispatchDebug,
      forward: fallbackExchange({ dispatchDebug }),
    })(operations$)
  );

  // Prevent the `results$` exchange pipeline from being closed by active
  // cancellations cascading up from components
  pipe(results$, publish);

  return client;
} as any;

export const createClient = (Client as any) as (opts: ClientOptions) => Client;
